import { Transition } from '@headlessui/react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import { HTTP_DIFT_ID_HEADER } from '@captaincause/lib/api/headers'
import { API_URL } from '@captaincause/lib/api/url'
import { useMutation } from '@captaincause/lib/api/useMutation'
import { mustBeRequired } from '@captaincause/lib/form/mustBeRequired'
import { Submit } from '@captaincause/ui/Button/Submit'
import { Form } from '@captaincause/ui/Form'
import { ScrollTop } from '@captaincause/ui/ScrollTop'
import { TextInputField } from '@captaincause/ui/TextInput/Field'
import { tm } from '@captaincause/ui/tm'
import { getProjectsFilters } from 'api/projects'
import { LightNavAndSmallFooterLayout } from 'layouts/LightNavAndSmallFooter'
import { getDift } from 'pages/api/dift'
import { DifterPageHead } from 'pages/Head'
import { injectI18n } from 'utils/i18n'
import { Button } from 'components/Button'
import { TYPES } from 'components/DiftType/types'
import { ProjectFiltersBar } from 'components/Project/Filtersbar'
import { UnveilDiftAnimation } from '../UnveilDiftAnimation'
import { DiftHeader } from './Header'
import { DiftProjectList } from './List'
import type { ThrowableApiErrorData } from '@captaincause/lib/api/errors'
import type {
  Dift as DiftType,
  Locales as Localestype,
  Project,
  ProjectFilters,
} from '@captaincause/types'
import type { FFFormApi, RFFProps } from '@captaincause/ui/Form'
import type { NextPageWithLayout } from 'app'
import type { ProjectFiltersBarImperativeRef } from 'components/Project/Filtersbar'
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next'

interface PageProps {
  dift: DiftType | null
  filters: ProjectFilters | null
  code: string | null
}
const LOCALIZATIONS = ['fr', 'en']

export const getServerSideProps: GetServerSideProps<
  PageProps
> = async context => {
  const localizations = LOCALIZATIONS.map(locale => ({ locale }))

  const locale = context.locale as Localestype

  const i18n = await injectI18n(locale, [
    'filters.component',
    'footer.component',
    'give-dift.page',
    'give.page',
    'metadata',
    'nav.component',
    'projects.page',
  ])

  const { id } = context.query
  const code = Array.isArray(id) ? null : id || null

  if (!code || code.length < 3 || code.length > 20) {
    return {
      props: {
        dift: null,
        filters: null,
        localizations,
        code,
        ...i18n,
      },
    }
  }

  try {
    const filters = await getProjectsFilters({ locale })

    const serverSideDift = await getDift({ locale, body: { code } })

    return {
      props: {
        dift: serverSideDift,
        code,
        filters,
        localizations,
        hideFooterCta: true,
        ...i18n,
      },
    }
  } catch (error: unknown) {
    console.error('error', error)

    return {
      props: {
        dift: null,
        filters: null,
        localizations,
        code,
        ...i18n,
      },
    }
  }
}

// ---

interface Values {
  code: string
}

interface CheckTriggerArgs extends Values {
  locale: Localestype
}

const initialValues: Values = {
  code: '',
}

type InferredPageProps = InferGetServerSidePropsType<typeof getServerSideProps>
const Dift: NextPageWithLayout<InferredPageProps> = ({
  dift: serverSideDift,
  filters,
  code,
}: PageProps) => {
  const { t } = useTranslation('give-dift.page')

  const filtersBarRef = useRef<ProjectFiltersBarImperativeRef>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [dift, setDift] = useState<DiftType | undefined>(undefined)
  const [displayProjects, setDisplayProjects] = useState<boolean>(false)
  const [showUnveilDift, setShowUnveilDift] = useState<boolean>(false)
  const [animationsDone, setAnimationsDone] = useState<string[]>([])

  const router = useRouter()

  const { trigger: triggerCheckDift } = useMutation<DiftType, CheckTriggerArgs>(
    {
      url: `${API_URL}/give/difts`,
      asParams: true,
      method: 'GET',
    },
  )

  const submit = async (values: Values) =>
    triggerCheckDift({
      code: values.code,
      locale: router.locale as Localestype,
    })

  function handleResult(result: DiftType) {
    setDift(result)
    setProjects(result.projects)
  }

  useEffect(() => {
    if (serverSideDift) {
      handleResult(serverSideDift)
      setShowUnveilDift(true)
    }
  }, [serverSideDift])

  const submitOnMount = (form: RFFProps<Values>) => {
    if (!code) return
    form.form.change('code', code)
    form.form.submit()
  }

  const submitSuccess = async (result: DiftType | undefined) => {
    if (result?.id) {
      await router.push(`/give/dift/${result.code}`, undefined, {
        shallow: true,
      })
      handleResult(result)
    }
  }

  const submitFail = async (
    error: ThrowableApiErrorData,
    values: Values,
    form: FFFormApi<Values>,
  ) => {
    form.restart()
    if (code) {
      await router.push(`/give/dift`, undefined, {
        shallow: true,
      })
    }
  }

  return (
    <div
      className={tm(
        serverSideDift &&
          !displayProjects &&
          'max-h-[calc(100vh-128px)] min-h-[calc(100vh-128px)]',
      )}
    >
      <DifterPageHead noIndex />

      <Transition
        show={!dift?.id && !serverSideDift}
        as="main"
        id="main"
        className="relative grid max-h-[calc(100vh-128px)] min-h-[calc(100vh-128px)] flex-1 transform-gpu grid-cols-1 bg-light-beige transition-transform ease-in-out data-[closed]:scale-95 data-[closed]:opacity-0 data-[leave]:duration-700"
        afterLeave={() => setShowUnveilDift(true)}
      >
        <div className="mx-auto mb-4 flex max-w-2xl flex-col justify-center px-6">
          <h1 className="mb-4 text-center font-default text-5xl font-bold md:text-left">
            {t('title')}
          </h1>
          <Form<Values, DiftType>
            onSubmit={submit}
            onMount={submitOnMount}
            onSubmitSuccess={submitSuccess}
            locale={router.locale as Localestype}
            onSubmitFail={submitFail}
            initialValues={initialValues}
            withToastOnFailure
          >
            <>
              <TextInputField
                name="code"
                label={t('code.label')}
                placeholder="CAPT123ABC"
                validators={{ mustBeRequired }}
                locale={router.locale as Localestype}
              />
              <div className="mt-4 flex justify-center">
                <Submit variant="primary" small full>
                  {t('submit')}
                </Submit>
              </div>
            </>
          </Form>

          <div className="mt-4 flex justify-end">
            <Button variant="tertiary" href="/me/buy">
              {t('offer')}
            </Button>
          </div>
        </div>
      </Transition>

      <Transition
        show={showUnveilDift && animationsDone.length !== 2}
        appear
        as="main"
        id="main"
        className={tm(
          'top-0 flex h-[calc(100vh-128px)] w-full flex-1 flex-col justify-center bg-light-beige transition-transform ease-out data-[closed]:translate-y-[10px] data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-700 data-[leave]:duration-500',
          dift && TYPES[dift?.type].colors.background,
        )}
        afterLeave={() => setDisplayProjects(true)}
      >
        <UnveilDiftAnimation
          dift={dift}
          setAnimationsDone={setAnimationsDone}
          skipEllipse
        />
      </Transition>

      <Transition
        show={displayProjects}
        as="main"
        id="main"
        className="data-[closed]:translation-y-[20px] transition-transform data-[closed]:opacity-0 data-[enter]:duration-700 data-[enter]:ease-out"
      >
        <DiftHeader dift={dift} />
        <div className="mx-auto mt-6 w-full max-w-screen-2xl flex-col-reverse items-center justify-between gap-8 px-4 md:flex-row md:items-start md:gap-12 md:px-8 lg:gap-20 lg:px-10 xl:gap-44 xl:px-20">
          {filters ? (
            <ProjectFiltersBar
              projects={projects}
              filters={filters}
              onFilter={filteredProjects => setProjects(filteredProjects)}
              ref={filtersBarRef}
              searchHTTPHeaders={
                dift ? [[HTTP_DIFT_ID_HEADER, dift.id]] : undefined
              }
              defaultThemeFilters={dift ? TYPES[dift.type].defaultFilters : []}
            />
          ) : null}
        </div>
        <DiftProjectList
          dift={dift}
          projects={projects}
          clearFilters={filtersBarRef?.current?.clearFilters}
        />
        <ScrollTop />
      </Transition>
    </div>
  )
}

Dift.Layout = LightNavAndSmallFooterLayout
Dift.pagePropsOnLayout = true

export default Dift
